import React, { useState } from "react";
import { ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { TASK } from "@/constants/testIds";

export default function DisputeCard({ task, onTaskUpdate }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  if (task.is_disputed) {
    const d = task.dispute || {};
    return (
      <div className="eb-card p-6">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-5 h-5 text-[var(--eb-secondary)]" />
          <h3 className="text-lg font-[800]">Dispute — {d.status}</h3>
        </div>
        <p className="text-sm mt-2 text-[var(--eb-text)]">&ldquo;{d.reason}&rdquo;</p>
        {d.status === "resolved" && (
          <div className="mt-3 text-xs text-[var(--eb-text-dim)]">
            Resolution: <span className="text-[var(--eb-text)] font-[700]">{d.decision?.replace(/_/g, " ")}</span>
            {d.note ? ` · ${d.note}` : ""}
          </div>
        )}
      </div>
    );
  }

  if (["completed", "cancelled"].includes(task.status)) return null;

  const submit = async () => {
    if (reason.trim().length < 4) {
      toast.error("Please describe what went wrong");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/tasks/${task.task_id}/dispute`, { reason });
      onTaskUpdate(data);
      toast.success("Dispute opened — our team will review.");
      setReason("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <details className="eb-card p-6 group">
      <summary className="cursor-pointer flex items-center gap-2 list-none">
        <ShieldAlert className="w-5 h-5 text-[var(--eb-secondary)]" />
        <span className="text-sm font-[700]">Something went wrong? Raise a dispute</span>
      </summary>
      <div className="mt-4 space-y-3">
        <textarea
          data-testid={TASK.disputeReason}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows="3"
          className="eb-input"
          placeholder="Tell us what happened. Our admin team will review and decide on refund or release."
        />
        <div className="flex justify-end">
          <button
            data-testid={TASK.disputeSubmit}
            onClick={submit}
            disabled={busy}
            className="eb-btn eb-btn-danger !py-2 !px-4 !text-sm"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldAlert className="w-4 h-4" />}
            {busy ? "Submitting…" : "Open dispute"}
          </button>
        </div>
      </div>
    </details>
  );
}
